import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCredits } from '../../hooks/useCredits';

export const CreditsSection: React.FC = () => {
  const { balance, creditState, loading, error, packs, packsLoading, purchaseCredits, purchasing } = useCredits();
  const [purchasingPack, setPurchasingPack] = useState<string | null>(null);

  const handlePurchase = async (packId: string) => {
    setPurchasingPack(packId);
    await purchaseCredits(packId);
    setPurchasingPack(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="h6">Credit Balance</Typography>
            </Box>

            <Typography
              variant="h2"
              color={creditState === 'empty' ? 'error' : creditState === 'low' ? 'warning.main' : 'primary'}
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              {balance.available}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              credits available
            </Typography>

            {creditState === 'low' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Running low on credits!
              </Alert>
            )}
            {creditState === 'empty' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                No credits remaining. Purchase more to continue using AI features.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon color="action" />
              <Typography variant="subtitle2" color="text.secondary">Lifetime Stats</Typography>
            </Box>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={`${balance.purchased} purchased`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon fontSize="small" color="info" />
                </ListItemIcon>
                <ListItemText
                  primary={`${balance.used} used`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              {balance.monthlyAllocation > 0 && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${balance.monthlyAllocation} monthly free`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Purchase Credits
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Credits never expire. Use them for AI-powered document analysis.
            </Typography>

            {packsLoading ? (
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
                  </Grid>
                ))}
              </Grid>
            ) : packs.length === 0 ? (
              <Alert severity="warning">
                Unable to load credit packs. Please try again later.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {packs.map((pack) => (
                  <Grid item xs={12} sm={6} key={pack.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        border: pack.badge ? '2px solid' : '1px solid',
                        borderColor: pack.badge === 'best_value' ? 'success.main' : pack.badge ? 'primary.main' : 'divider',
                      }}
                    >
                      {pack.badge && (
                        <Chip
                          label={pack.badge === 'best_value' ? 'Best Value' : 'Popular'}
                          color={pack.badge === 'best_value' ? 'success' : 'primary'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 16,
                          }}
                        />
                      )}
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {pack.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {pack.credits} credits
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" color="primary" fontWeight={700}>
                              {pack.priceFormatted}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pack.pricePerCredit}/credit
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="contained"
                          color={pack.badge === 'best_value' ? 'success' : 'primary'}
                          fullWidth
                          sx={{ mt: 2 }}
                          onClick={() => handlePurchase(pack.id)}
                          disabled={purchasing}
                          startIcon={purchasingPack === pack.id ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
                        >
                          {purchasingPack === pack.id ? 'Processing...' : 'Buy Now'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
